/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import * as _ from 'lodash';
import {catchError, finalize, map, Observable, of, shareReplay, throwError} from 'rxjs';

import {ApiConfig} from '../../config/api-config';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {AdnApiClientService} from './adn-api-client.service';

@Injectable({providedIn: 'root'})
export class DataViewApiService {
  private readonly lineItemViews: any[] = [];
  private readonly currentLineItemCalls = new Map<string, Observable<any>>();

  constructor(
    private readonly adnLog: AdnLogService,
    private readonly client: AdnApiClientService,
    private readonly apiConfig: ApiConfig
  ) {
  }

  listView(viewModel: string, options: Record<string, any>, parameters?: any): Observable<any> {
    const normalizedParameters = this.normalizeParameters(parameters);
    return this.client.request<any>({
      method: 'POST',
      url: this.apiConfig.getVersionedUri('dataview'),
      body: [{viewModel, parameters: normalizedParameters}],
      params: options
    }).pipe(
      map((data) => {
        if (data.results?.length !== 1) {
          throw new Error('Expected only one data-view result');
        }
        const result = data.results[0];
        if (result.viewModel !== viewModel) {
          throw new Error('Unexpected data-view model');
        }
        return result.dataView;
      }),
      catchError((error) => {
        this.adnLog.warn('DataView.getView', error);
        return throwError(() => error);
      })
    );
  }

  getView(viewModel: string, parameters?: any, includeStats?: boolean): Observable<any> {
    const normalizedParameters = this.normalizeParameters(parameters);
    if (viewModel === 'LineItemView' && !normalizedParameters.id) {
      this.adnLog.warn('DataView.get LineItemView called without parameters.id', normalizedParameters);
      return throwError(() => 'DataView.get LineItemView requires parameters.id');
    }

    const request = this.client.request<any>({
      method: 'POST',
      url: this.apiConfig.getVersionedUri('dataview'),
      body: [{viewModel, parameters: normalizedParameters}],
      params: {includeStats}
    }).pipe(
      map((data) => {
        if (data.results?.length !== 1) {
          throw new Error('Expected only one data-view result');
        }
        const result = data.results[0];
        if (result.viewModel !== viewModel) {
          throw new Error('Expected viewModel ' + viewModel + ' but got ' + result.viewModel);
        }
        const results = result.dataView?.results || result.dataView || {};
        if (viewModel === 'LineItemView' && results.lineItem) {
          this.cacheLineItemView(results);
        }
        return results;
      }),
      catchError((response) => {
        this.adnLog.warn('DataView.get', response);
        return throwError(() => 'Error returned from DataView');
      })
    );

    const id = viewModel === 'LineItemView' ? normalizedParameters.id : undefined;
    if (!id) {
      return request;
    }

    const sharedRequest = request.pipe(
      finalize(() => {
        if (this.currentLineItemCalls.get(id) === sharedRequest) {
          this.currentLineItemCalls.delete(id);
        }
      }),
      shareReplay({bufferSize: 1, refCount: false})
    );
    this.currentLineItemCalls.set(id, sharedRequest);
    return sharedRequest;
  }

  getLineItemViewFromCache(id: string): Observable<any> {
    if (!id) {
      return throwError(() => 'DataView.getFromCache requires an id');
    }
    const currentCall = this.currentLineItemCalls.get(id);
    if (currentCall) {
      return currentCall;
    }
    const cached = this.lineItemViews.find((view) => view?.lineItem?.id === id);
    return cached ? of(cached) : this.getView('LineItemView', {id}, false);
  }

  private cacheLineItemView(results: any): void {
    const existingIndex = this.lineItemViews.findIndex((view) => view?.lineItem?.id === results.lineItem.id);
    if (existingIndex > -1) {
      this.lineItemViews.splice(existingIndex, 1);
    }
    const limitedResult = _.pick(_.cloneDeep(results), ['advertiser', 'lineItem', 'creatives', 'order']);
    if (limitedResult.lineItem?.id) {
      limitedResult.lineItem = _.pick(limitedResult.lineItem, ['id', 'name']);
    }
    if (limitedResult.advertiser?.id) {
      limitedResult.advertiser = _.pick(limitedResult.advertiser, ['id', 'name', 'advertiserUrl']);
    }
    if (limitedResult.order?.id) {
      limitedResult.order = _.pick(limitedResult.order, ['id', 'name']);
    }
    limitedResult.creatives = _.map(limitedResult.creatives, (creative) => {
      const creativeData = _.pick(creative, [
        'id', 'name', 'width', 'height', 'layoutParameters', 'constraintsToUrls',
        'layout.id', 'layout.name', 'layout.detectedCategory', 'objectState'
      ]);
      creativeData['url'] = _.values(creativeData['constraintsToUrls'] || {})[0] || '';
      if (!creativeData['url'] && creative['assetHtmlUrls']) {
        creativeData['url'] = _.values(_.values(creative['assetHtmlUrls'] || {})[0] || {})[0] || '';
      }
      return creativeData;
    });
    this.lineItemViews.push(limitedResult);
    if (this.lineItemViews.length > 50) {
      this.lineItemViews.splice(0, this.lineItemViews.length - 50);
    }
  }

  private normalizeParameters(parameters: any): Record<string, any> {
    if (parameters && !_.isPlainObject(parameters)) {
      return {id: parameters};
    }
    const normalized = {...(parameters || {})};
    if (_.isUndefined(normalized.id) && _.has(normalized, 'id')) {
      delete normalized.id;
    }
    return normalized;
  }
}
